import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe publishable key
// Note: API key is hardcoded here due to .env file encoding issues on Windows
// Production key: pk_live_51SOhaD0tboMIqtPiuuoKDyfG8MazUGD6ej4N3rFGJCefvRqub3tRlMj7b3pXanxfgUjmhkgYjuNJyqs70fIytDWG00B6obeVsc
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51SOhaD0tboMIqtPiuuoKDyfG8MazUGD6ej4N3rFGJCefvRqub3tRlMj7b3pXanxfgUjmhkgYjuNJyqs70fIytDWG00B6obeVsc';

// Initialize Stripe
let stripePromise: Promise<any> | null = null;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Get Firebase Functions URL
const getFunctionsUrl = () => {
  // Get project ID from environment or use default
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  
  // Always use deployed functions URL
  // For local testing with emulators, uncomment the line below and ensure emulators are running
  // if (import.meta.env.DEV && window.location.hostname === 'localhost') {
  //   return `http://localhost:5001/${projectId}/us-central1`;
  // }
  
  return `https://us-central1-${projectId}.cloudfunctions.net`;
};

// Create checkout session
export const createCheckoutSession = async (data: {
  plan: string;
  userId: string;
  organizationId: string; // Required now
  locationId?: string;
  couponCode?: string;
  quantity?: number; // Number of locations/licenses (optional - will be calculated if not provided)
}) => {
  try {
    const functionsUrl = getFunctionsUrl();
    const endpoint = `${functionsUrl}/createCheckoutSession`;
    
    console.log('Creating checkout session:', { endpoint, data, origin: window.location.origin });
    
    // Include origin header so the function can use it for success/cancel URLs
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    // Add origin header if available
    if (typeof window !== 'undefined' && window.location.origin) {
      headers['Origin'] = window.location.origin;
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create checkout session';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        // If response is not JSON, get text
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      console.error('Checkout session error:', { 
        status: response.status, 
        statusText: response.statusText, 
        errorMessage,
        endpoint 
      });
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Checkout session created:', result);
    
    // Ensure we have the URL for redirection
    if (!result.url && !result.sessionId) {
      throw new Error('No checkout URL or session ID returned from server');
    }
    
    return result;
  } catch (error: any) {
    console.error('Error creating checkout session:', error);
    
    // Provide more helpful error messages
    if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
      const endpoint = `${getFunctionsUrl()}/createCheckoutSession`;
      const troubleshootingTips = [
        '1. Check your internet connection',
        '2. Verify Firebase Functions are deployed: firebase functions:list',
        `3. Test the endpoint directly: ${endpoint}`,
        '4. Check browser console for CORS errors',
        '5. Ensure you are not blocking requests with an ad blocker'
      ].join('\n');
      
      throw new Error(
        `Cannot connect to payment server.\n\n${troubleshootingTips}\n\nEndpoint: ${endpoint}`
      );
    }
    
    throw error;
  }
};

// Redirect to Stripe Checkout
// Note: Using manual redirect instead of deprecated stripe.redirectToCheckout()
// The server returns session.url which we use directly for redirection
export const redirectToCheckout = async (checkoutUrl: string) => {
  try {
    if (!checkoutUrl) {
      throw new Error('Checkout URL is required');
    }

    // Validate it's a Stripe checkout URL
    if (!checkoutUrl.startsWith('https://checkout.stripe.com/')) {
      throw new Error('Invalid checkout URL format');
    }

    // Redirect manually to the Stripe Checkout page
    window.location.href = checkoutUrl;
  } catch (error: any) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

// Create portal session (for managing subscriptions)
export const createPortalSession = async (userId: string, organizationId: string) => {
  try {
    const functionsUrl = getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/createPortalSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, organizationId }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create portal session';
      let errorDetails = '';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
        errorDetails = error.details || error.hint || '';
      } catch {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      
      const fullError = errorDetails 
        ? `${errorMessage}\n\n${errorDetails}` 
        : errorMessage;
      throw new Error(fullError);
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

// Redirect to customer portal
export const redirectToPortal = async (userId: string, organizationId: string) => {
  try {
    const result = await createPortalSession(userId, organizationId);
    if (result.url) {
      window.location.href = result.url;
    }
  } catch (error: any) {
    console.error('Error redirecting to portal:', error);
    throw error;
  }
};

// Sync subscription from Stripe to Firestore
export const syncSubscription = async (organizationId: string, subscriptionId?: string) => {
  try {
    const functionsUrl = getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/syncSubscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ organizationId, subscriptionId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sync subscription');
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error syncing subscription:', error);
    throw error;
  }
};

// Verify checkout session and sync subscription
export const verifyCheckoutSession = async (sessionId: string) => {
  try {
    const functionsUrl = getFunctionsUrl();
    const response = await fetch(`${functionsUrl}/verifyCheckoutSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to verify checkout session');
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error verifying checkout session:', error);
    throw error;
  }
};

// Update subscription quantity
export const updateSubscriptionQuantity = async (organizationId: string, quantity: number) => {
  try {
    const functionsUrl = getFunctionsUrl();
    const endpoint = `${functionsUrl}/updateSubscriptionQuantity`;
    
    console.log('Updating subscription quantity:', { endpoint, organizationId, quantity });
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ organizationId, quantity }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to update subscription quantity';
      let errorDetails = '';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
        errorDetails = error.details || '';
      } catch {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
      console.error('Subscription quantity update error:', { 
        status: response.status, 
        statusText: response.statusText, 
        errorMessage,
        errorDetails,
        endpoint,
        organizationId,
        quantity
      });
      
      const fullErrorMessage = errorDetails 
        ? `${errorMessage}\n\n${errorDetails}` 
        : errorMessage;
      throw new Error(fullErrorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error updating subscription quantity:', error);
    
    // Provide more helpful error messages
    if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
      const endpoint = `${getFunctionsUrl()}/updateSubscriptionQuantity`;
      const troubleshootingTips = [
        '1. Check your internet connection',
        '2. Verify Firebase Functions are deployed: firebase deploy --only functions:updateSubscriptionQuantity',
        `3. Test the endpoint directly: ${endpoint}`,
        '4. Check browser console for CORS errors',
        '5. Ensure you are not blocking requests with an ad blocker'
      ].join('\n');
      
      throw new Error(
        `Cannot connect to subscription update server.\n\n${troubleshootingTips}\n\nEndpoint: ${endpoint}`
      );
    }
    
    throw error;
  }
};

